import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  MaintenanceTable,
  TableCell,
  TableCellValue,
  TableCellLabel,
  MaintenanceItemContainer,
  HistoryTable,
  HistoryTableCell,
  HistoryItemContainer,
  ButtonContainer,
  EditButton,
} from './style'
import { BlurBackground } from '../../style'
import cross from '../../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../../assets/edit-pen-white.svg'

const API_BASE_URL = `${process.env.REACT_APP_EQUIPMENT_BASE_URL}`

interface ViewEquipmentModalProps {
  machineId: number
  onClose: () => void
}

interface BackendPeriodicMaintenanceItem {
  id?: number
  maintenanceName: string
  periodicityValue: number
  periodicityUnit: 'm' | 'km' | 'days' | 'hours' | 'months' | ''
  isVerificationEnabled: boolean
  verificationValue?: number
  verificationUnit?: 'hours' | 'days' | 'months' | ''
  isInternalService: boolean
  selectedServiceProviders: string[]
}

interface BackendMaintenanceHistoryItem {
  id: number
  maintenanceName: string
  maintenanceDate: string
  value: number
  type: string
}

interface BackendMachineDetail {
  code: string
  name: string
  model: string
  manufacturer: string
  year: number
  type: string
  dimension: string
  weight: number
  location: string
  costPerHour: number
  capacity: string
  selectedServiceProviders: string[]
  periodicMaintenances: BackendPeriodicMaintenanceItem[]
  maintenanceHistory: BackendMaintenanceHistoryItem[]
}

interface MachineDetailItem {
  label: string
  value: string
}

interface PeriodicMaintenanceItem {
  name: string
  periodicity: string
  verification: string
}

interface MaintenanceHistoryItem {
  name: string
  date: string
  value: string
  type: string
}

interface FullMachineData {
  general: MachineDetailItem[]
  operation: MachineDetailItem[]
  maintenanceService: MachineDetailItem[]
  periodicMaintenance: PeriodicMaintenanceItem[]
  maintenanceHistory: MaintenanceHistoryItem[]
}

const ViewEquipmentModal: React.FC<ViewEquipmentModalProps> = ({
  machineId,
  onClose,
}) => {
  const navigate = useNavigate()

  const [machineData, setMachineData] = useState<FullMachineData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const formatCurrency = useCallback(
    (value: number | string | undefined | null): string => {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (
        isNaN(numValue as number) ||
        numValue === null ||
        numValue === undefined
      ) {
        return 'N/A'
      }
      return numValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    [],
  )

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        const [year, month, day] = dateString.split('-').map(Number)
        if (year && month && day) {
          const parsedDate = new Date(year, month - 1, day)
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('pt-BR')
          }
        }
        return dateString
      }
      return date.toLocaleDateString('pt-BR')
    } catch (e) {
      console.warn('Could not parse date string:', dateString, e)
      return dateString
    }
  }, [])

  const getServiceProviderName = useCallback(
    (providerId: string, isInternal: boolean): string => {
      const employeeOptions = [
        { value: 'provider1', label: 'Fernando Fernandes' },
        { value: 'provider2', label: 'Maria Silva' },
      ]
      const officeOptions = [
        { value: 'office1', label: 'Oficina do Zé' },
        { value: 'office2', label: 'Auto Elétrica Avenida' },
      ]

      const options = isInternal ? employeeOptions : officeOptions
      const found = options.find((opt) => opt.value === providerId)
      return found ? found.label : 'Desconhecido'
    },
    [],
  )

  useEffect(() => {
    const fetchMachineData = async () => {
      if (!machineId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) {
        setError('Sessão expirada. Faça login novamente.')
        setLoading(false)
        navigate('/login')
        return
      }

      try {
        const response = await axios.get<BackendMachineDetail>(
          `${API_BASE_URL}/equipment/${machineId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const backendData = response.data

        const mappedGeneral: MachineDetailItem[] = [
          { label: 'Código', value: backendData.code || 'N/A' },
          { label: 'Nome', value: backendData.name || 'N/A' },
          { label: 'Modelo', value: backendData.model || 'N/A' },
          { label: 'Fabricante', value: backendData.manufacturer || 'N/A' },
          { label: 'Ano', value: backendData.year?.toString() || 'N/A' },
          { label: 'Tipo', value: backendData.type || 'N/A' },
          { label: 'Dimensão', value: backendData.dimension || 'N/A' },
          { label: 'Peso', value: backendData.weight?.toString() || 'N/A' },
          { label: 'Local', value: backendData.location || 'N/A' },
        ]

        const mappedOperation: MachineDetailItem[] = [
          {
            label: 'Custo/Hora',
            value: formatCurrency(backendData.costPerHour),
          },
          { label: 'Capacidade', value: backendData.capacity || 'N/A' },
        ]

        const mappedMaintenanceService: MachineDetailItem[] = [
          {
            label: 'Prestador de Serviço Global',
            value:
              backendData.selectedServiceProviders &&
              backendData.selectedServiceProviders.length > 0
                ? backendData.selectedServiceProviders
                    .map((id) => getServiceProviderName(id, false))
                    .join(', ')
                : 'N/A',
          },
        ]

        const mappedPeriodicMaintenance: PeriodicMaintenanceItem[] = (
          backendData.periodicMaintenances || []
        ).map((item) => ({
          name: item.maintenanceName || 'N/A',
          periodicity: `${item.periodicityValue || 'N/A'} ${
            item.periodicityUnit || ''
          }`.trim(),
          verification: item.isVerificationEnabled
            ? `${item.verificationValue || 'N/A'} ${
                item.verificationUnit || ''
              }`.trim()
            : 'Desabilitado',
        }))

        const mappedMaintenanceHistory: MaintenanceHistoryItem[] = (
          backendData.maintenanceHistory || []
        ).map((item) => ({
          name: item.maintenanceName || 'N/A',
          date: formatDate(item.maintenanceDate),
          value: item.value?.toString() || 'N/A',
          type: item.type || 'N/A',
        }))

        setMachineData({
          general: mappedGeneral,
          operation: mappedOperation,
          maintenanceService: mappedMaintenanceService,
          periodicMaintenance: mappedPeriodicMaintenance,
          maintenanceHistory: mappedMaintenanceHistory,
        })
      } catch (err) {
        console.error('Erro ao buscar dados da máquina:', err)
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          navigate('/login')
        } else if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('Máquina não encontrada.')
        } else {
          setError(
            'Falha ao carregar os dados da máquina. Por favor, tente novamente.',
          )
        }
      } finally {
        setLoading(false)
      }
    }

    fetchMachineData()
  }, [machineId, navigate, formatCurrency, formatDate, getServiceProviderName])

  const handleClose = () => {
    onClose()
  }

  const handleEditMachine = () => {
    navigate(`/panel/equipment/edit/${machineId}`)
    onClose()
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados da máquina...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Carregando informações da máquina...</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p style={{ color: 'red' }}>{error}</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!machineData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Máquina não encontrada</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Nenhum dado encontrado para a máquina com ID: {machineId}.</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const machineCodeItem = machineData.general.find(
    (item) => item.label === 'Código',
  )
  const displayCode = machineCodeItem
    ? machineCodeItem.value
    : `MP-${String(machineId).padStart(5, '0')}`

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Máquina: {displayCode}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {machineData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Operação</SectionHeader>
            <DetailGrid>
              {machineData.operation.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Serviço de manutenção</SectionHeader>
            <DetailGrid>
              {machineData.maintenanceService.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Manutenção periódica</SectionHeader>
            <MaintenanceTable>
              {machineData.periodicMaintenance.length > 0 ? (
                machineData.periodicMaintenance.map((item, index) => (
                  <MaintenanceItemContainer key={index}>
                    <TableCell>
                      <TableCellLabel>Nome</TableCellLabel>
                      <TableCellValue>{item.name}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Periodicidade</TableCellLabel>
                      <TableCellValue>{item.periodicity}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Verificação</TableCellLabel>
                      <TableCellValue>{item.verification}</TableCellValue>
                    </TableCell>
                  </MaintenanceItemContainer>
                ))
              ) : (
                <p
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  Nenhuma manutenção periódica registrada.
                </p>
              )}
            </MaintenanceTable>
          </Section>

          <Section>
            <SectionHeader>Histórico de manutenção</SectionHeader>
            <HistoryTable>
              {machineData.maintenanceHistory.length > 0 ? (
                machineData.maintenanceHistory.map((item, index) => (
                  <HistoryItemContainer key={index}>
                    <HistoryTableCell>
                      <TableCellLabel>Nome</TableCellLabel>
                      <TableCellValue>{item.name}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Data</TableCellLabel>
                      <TableCellValue>{item.date}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Valor</TableCellLabel>
                      <TableCellValue>{item.value}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Tipo</TableCellLabel>
                      <TableCellValue>{item.type}</TableCellValue>
                    </HistoryTableCell>
                  </HistoryItemContainer>
                ))
              ) : (
                <p
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  Nenhum histórico de manutenção encontrado.
                </p>
              )}
            </HistoryTable>
          </Section>
        </ContentContainer>

        <ButtonContainer>
          <EditButton onClick={handleEditMachine}>
            <img src={editIcon} alt='Editar' />
            Editar máquina
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
  )
}

export default ViewEquipmentModal